import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import logger from '../utils/logger';
import { watchIdToken, loginWithMicrosoft, logout } from './firebase';
import apiService from '../services/api';

interface AuthContextValue {
  user: any | null;
  idToken: string;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [idToken, setIdToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    logger.debug('AuthProvider: Initializing auth listener (using onIdTokenChanged for auto-refresh)');
    let mounted = true;
    
    // Set a timeout to ensure loading doesn't hang indefinitely
    const timeoutId = setTimeout(() => {
      if (mounted) {
        logger.debug('AuthProvider: Timeout reached, setting loading to false');
        setLoading(false);
        // Don't set error on timeout - just continue without auth
      }
    }, 5000); // Reduced to 5 second timeout
    
    // Use onIdTokenChanged instead of onAuthStateChanged to catch:
    // 1. Initial login
    // 2. Logout
    // 3. Automatic token refresh (~every 55 minutes)
    const unsub = watchIdToken(async (u) => {
      try {
        logger.debug('AuthProvider: Auth state changed', u ? 'User found' : 'No user');
        if (!mounted) return;
        
        setUser(u);
        const token = u ? await u.getIdToken() : '';
        setIdToken(token);
        if (token) apiService.setAuthToken(token); else apiService.clearAuthToken();
        
        clearTimeout(timeoutId);
        setError(null); // Clear any previous errors
      } catch (e: any) {
        logger.error('AuthProvider: Auth error', e);
        if (mounted) {
          setError(e.message || "We're having trouble signing you in. Please refresh the page and try again.");
          clearTimeout(timeoutId);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    });
    
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      unsub();
    };
  }, []);

  const signIn = async () => {
    try {
      const { user: u, idToken: t } = await loginWithMicrosoft();
      setUser(u);
      setIdToken(t);
      apiService.setAuthToken(t);
    } catch (e: any) {
      setError(e.message || 'Your sign-in attempt failed. Please try again or contact admin if the problem persists.');
    }
  };

  const signOut = async () => {
    await logout();
    setUser(null);
    setIdToken('');
    apiService.clearAuthToken();
  };

  return (
    <AuthContext.Provider value={{ user, idToken, loading, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Named export only